import { useCallback, useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertTriangle,
  BookOpen,
  Boxes,
  Calendar,
  ClipboardList,
  Download,
  Eye,
  FileText,
  GitBranch,
  Globe,
  Lock,
  PlayCircle,
  RefreshCw,
  ShieldCheck,
  Shield,
  Sparkles,
  Upload,
  Users,
  Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import { resetWelcomeTour } from '@/components/WelcomeTour';
import { manualAPI } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_INFO = {
  product_name: 'CG Business ERP',
  version: '3.5.0',
  semantic_version: '3.5.0',
  date_version: '2026.03.18.1',
  edition: 'Go-Live Operacional',
  modules: 24,
  access_levels: 12,
  last_updated: '--/--/----',
  go_live_status: 'Sistema em operação diária com histórico activo de versões',
  release_title: 'Actualização operacional do ERP',
  release_summary: 'Sem resumo publicado ainda.',
  manual_focus: 'Manual vivo por perfil com histórico de versões, módulos e rotinas-chave.',
  release_notes: [],
  release_history: [],
  profile_guides: [],
  module_overview: [],
  documentation_sync: {},
  profiles_documented: [],
  modules_documented: [],
};

const EMPTY_TRAINING_DATA = {
  items: [],
  meta: {
    total_documents: 0,
    mandatory_documents: 0,
    pending_required_documents: 0,
    pending_read_documents: 0,
    pending_accept_documents: 0,
    can_manage: false,
    can_report: false,
    categories: [],
    filters: {
      search: '',
      category: '',
      version: '',
    },
    dashboard: {
      total_users: 0,
      documents: [],
    },
    system_meta: {
      system_version: '',
      last_deploy_at: '',
      environment: '',
    },
  },
};

const TRAINING_CATEGORY_OPTIONS = [
  'Inventário',
  'Compras',
  'Vendas',
  'RH',
  'Financeiro',
  'Procedimentos Operacionais',
  'Segurança e TI',
];

const buildEditorState = (info) => ({
  release_title: info.release_title || '',
  release_summary: info.release_summary || '',
  edition: info.edition || '',
  go_live_status: info.go_live_status || '',
  manual_focus: info.manual_focus || '',
  release_notes_text: (info.release_notes || []).map((note) => note.summary || note.title).join('\n'),
});

const buildPayloadFromEditor = (editorState) => ({
  release_title: editorState.release_title.trim(),
  release_summary: editorState.release_summary.trim(),
  edition: editorState.edition.trim(),
  go_live_status: editorState.go_live_status.trim(),
  manual_focus: editorState.manual_focus.trim(),
  release_notes: editorState.release_notes_text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean),
});

const MiniStat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white/90 px-3 py-3" data-testid={testId}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
      <Icon className="h-3.5 w-3.5 text-[#2B4DAA]" />
      <span>{label}</span>
    </div>
    <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
  </div>
);

const downloadBlobFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const getFilenameFromDisposition = (contentDisposition, fallback) => {
  if (!contentDisposition) return fallback;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] || fallback;
};

const formatFileSize = (bytes = 0) => {
  const size = Number(bytes || 0);
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${size} B`;
};

const formatDateTimeLabel = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pt-PT');
  } catch {
    return value;
  }
};

const ProfileWorkflowCard = ({ guide, guideIndex }) => (
  <div className="rounded-[24px] border border-slate-200 bg-white/90 p-3" data-testid={`about-profile-workflow-${guideIndex}`}>
    <div className="flex flex-col gap-2 border-b border-slate-200 pb-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <div className="text-sm font-semibold text-slate-950">{guide.title}</div>
        <div className="mt-1 text-xs text-slate-500">Fluxos screen-by-screen para {guide.title.toLowerCase()}.</div>
      </div>
      <Badge variant="outline" className="w-fit rounded-full">{guide.screen_flows?.length || 0} fluxos</Badge>
    </div>

    {(guide.screen_flows || []).map((flow, flowIndex) => (
      <div key={`${guide.role_key}-flow-${flowIndex}`} className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`about-profile-workflow-flow-${guideIndex}-${flowIndex}`}>
        <div className="flex flex-col gap-2 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-950">{flow.title}</div>
            <p className="mt-1 text-xs text-slate-600">{flow.goal}</p>
          </div>
          <Badge variant="outline" className="w-fit rounded-full text-[11px]">{flow.steps?.length || 0} passos</Badge>
        </div>

        <div className="mt-3 grid gap-2 xl:grid-cols-2">
          {(flow.steps || []).map((step, stepIndex) => (
            <div key={`${guide.role_key}-flow-${flowIndex}-step-${stepIndex}`} className="rounded-xl border border-slate-200 bg-white px-3 py-3" data-testid={`about-profile-workflow-step-${guideIndex}-${flowIndex}-${stepIndex}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Passo {stepIndex + 1}</span>
                <Badge variant="outline" className="rounded-full text-[11px]">{step.screen}</Badge>
              </div>
              <div className="mt-2 text-xs text-slate-500">{step.route}</div>
              <p className="mt-2 text-sm text-slate-700">{step.action}</p>
              <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-xs text-emerald-700">
                <strong>Check:</strong> {step.check}
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default function AboutPage() {
  const { user, hasPermission } = useAuth();
  const [manualInfo, setManualInfo] = useState(EMPTY_INFO);
  const [trainingData, setTrainingData] = useState(EMPTY_TRAINING_DATA);
  const [loading, setLoading] = useState(true);
  const [trainingLoading, setTrainingLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingProfileRole, setDownloadingProfileRole] = useState('');
  const [activeTrainingActionKey, setActiveTrainingActionKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('update');
  const [editorState, setEditorState] = useState({});
  const [selectedProfileRole, setSelectedProfileRole] = useState('');
  const [trainingUploadOpen, setTrainingUploadOpen] = useState(false);
  const [trainingFilters, setTrainingFilters] = useState({
    search: '',
    category: '',
    version: '',
  });
  const [trainingUploadState, setTrainingUploadState] = useState({
    title: '',
    category: 'Procedimentos Operacionais',
    description: '',
    version_label: '',
    notes: '',
    document_id: '',
    is_mandatory: true,
    publish_now: true,
    file: null,
  });

  const canManageRelease = hasPermission('settings.edit');
  const canManageTraining = hasPermission(['about.training.manage', 'settings.edit']);
  const canViewTrainingReports = hasPermission(['about.training.reports', 'about.training.manage', 'settings.edit']);
  const releaseNotes = manualInfo.release_notes || [];
  const releaseHistory = manualInfo.release_history || [];
  const moduleOverview = manualInfo.module_overview || [];
  const profileGuides = useMemo(() => manualInfo.profile_guides || [], [manualInfo.profile_guides]);
  const documentationSync = manualInfo.documentation_sync || {};
  const trainingDocuments = trainingData.items || [];
  const trainingMeta = trainingData.meta || EMPTY_TRAINING_DATA.meta;
  const trainingDashboard = trainingMeta.dashboard || EMPTY_TRAINING_DATA.meta.dashboard;
  const trainingSystemMeta = trainingMeta.system_meta || EMPTY_TRAINING_DATA.meta.system_meta;
  const availableTrainingCategories = trainingMeta.categories?.length ? trainingMeta.categories : TRAINING_CATEGORY_OPTIONS;
  const publishedVersionCount = trainingDocuments.reduce((count, document) => count + (document.versions || []).filter((version) => version.is_published).length, 0);
  const acceptedRequiredCount = trainingDocuments.reduce((count, document) => {
    const pendingRequiredVersion = (document.versions || []).find((version) => version.pending_acceptance);
    return count + (document.is_mandatory && !pendingRequiredVersion ? 1 : 0);
  }, 0);
  const mandatoryCompletionPercent = trainingMeta.mandatory_documents > 0 ? Math.round((acceptedRequiredCount / trainingMeta.mandatory_documents) * 100) : 100;
  const resolvedSelectedProfileRole = useMemo(() => {
    if (!profileGuides.length) return '';
    if (profileGuides.some((guide) => guide.role_key === selectedProfileRole)) return selectedProfileRole;
    return profileGuides[0].role_key;
  }, [profileGuides, selectedProfileRole]);
  const selectedProfileGuide = useMemo(() => profileGuides.find((guide) => guide.role_key === selectedProfileRole) || profileGuides[0] || null, [profileGuides, selectedProfileRole]);
  const currentProfileGuide = useMemo(() => {
    if (!user) return null;
    const candidates = [user.custom_role_key, user.role, user.custom_role_name, user.job_title]
      .map((item) => String(item || '').trim().toLowerCase())
      .filter(Boolean);
    return profileGuides.find((guide) => {
      const guideCandidates = [guide.role_key, guide.title]
        .map((item) => String(item || '').trim().toLowerCase())
        .filter(Boolean);
      return candidates.some((candidate) => guideCandidates.includes(candidate));
    }) || null;
  }, [profileGuides, user]);

  const versionLabel = useMemo(() => {
    const semanticVersion = manualInfo.semantic_version || manualInfo.version;
    if (!semanticVersion) return '--';
    return `v${semanticVersion}`;
  }, [manualInfo.semantic_version, manualInfo.version]);

  const currentEditorDefaults = useMemo(() => buildEditorState(manualInfo), [manualInfo]);
  const resolvedEditorState = useMemo(() => ({ ...currentEditorDefaults, ...editorState }), [currentEditorDefaults, editorState]);

  const handleDownloadManual = () => {
    setDownloading(true);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${API_URL}/api/manual/download`;
    document.body.appendChild(iframe);

    setTimeout(() => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      setDownloading(false);
      toast.success('Download do manual iniciado com sucesso.');
    }, 1800);
  };

  const handleDownloadDoc = (path, successLabel) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${API_URL}${path}`;
    document.body.appendChild(iframe);
    setTimeout(() => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      toast.success(successLabel);
    }, 1500);
  };

  const loadTrainingDocuments = useCallback(async () => {
    setTrainingLoading(true);
    try {
      const response = await manualAPI.listTrainingDocuments({
        search: trainingFilters.search || undefined,
        category: trainingFilters.category || undefined,
        version: trainingFilters.version || undefined,
      });
      setTrainingData(response?.data || EMPTY_TRAINING_DATA);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível carregar a documentação e formação.');
    } finally {
      setTrainingLoading(false);
    }
  }, [trainingFilters.category, trainingFilters.search, trainingFilters.version]);

  const handleOpenTrainingDocument = async (documentId, versionId, title) => {
    const actionKey = `open-${documentId}-${versionId}`;
    setActiveTrainingActionKey(actionKey);
    try {
      const response = await manualAPI.openTrainingDocument(documentId, versionId, 'inline');
      const url = window.URL.createObjectURL(response.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(url), 120000);
      toast.success(`Documento aberto: ${title}`);
      await loadTrainingDocuments();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível abrir o documento.');
    } finally {
      setActiveTrainingActionKey('');
    }
  };

  const handleDownloadTrainingDocument = async (documentId, versionId, title, fallbackFilename) => {
    const actionKey = `download-${documentId}-${versionId}`;
    setActiveTrainingActionKey(actionKey);
    try {
      const response = await manualAPI.openTrainingDocument(documentId, versionId, 'attachment');
      downloadBlobFile(response.data, fallbackFilename || `${title}.pdf`);
      toast.success(`Download iniciado: ${title}`);
      await loadTrainingDocuments();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível descarregar o documento.');
    } finally {
      setActiveTrainingActionKey('');
    }
  };

  const handleAcceptTrainingDocument = async (documentId, versionId, title) => {
    const actionKey = `accept-${documentId}-${versionId}`;
    setActiveTrainingActionKey(actionKey);
    try {
      await manualAPI.acceptTrainingDocument(documentId, versionId);
      toast.success(`Aceite registado para ${title}`);
      await loadTrainingDocuments();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível confirmar o aceite.');
    } finally {
      setActiveTrainingActionKey('');
    }
  };

  const handleExportTrainingReport = async () => {
    setActiveTrainingActionKey('training-report');
    try {
      const response = await manualAPI.exportTrainingReport();
      downloadBlobFile(response.data, 'training-documents-report.csv');
      toast.success('Relatório de leitura e aceite exportado.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível exportar o relatório.');
    } finally {
      setActiveTrainingActionKey('');
    }
  };

  const handleTogglePublication = async (documentId, versionId, nextValue) => {
    const actionKey = `publish-${documentId}-${versionId}`;
    setActiveTrainingActionKey(actionKey);
    try {
      await manualAPI.setTrainingDocumentPublication(documentId, versionId, nextValue);
      toast.success(nextValue ? 'Versão publicada com sucesso.' : 'Versão despublicada com sucesso.');
      await loadTrainingDocuments();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível actualizar a publicação.');
    } finally {
      setActiveTrainingActionKey('');
    }
  };

  const handleTrainingUploadSubmit = async () => {
    if (!trainingUploadState.title.trim()) {
      toast.error('Título do documento é obrigatório.');
      return;
    }
    if (!trainingUploadState.file) {
      toast.error('Seleccione um PDF para carregar.');
      return;
    }

    const actionKey = 'training-upload';
    setActiveTrainingActionKey(actionKey);
    try {
      const formData = new FormData();
      formData.append('title', trainingUploadState.title);
      formData.append('category', trainingUploadState.category);
      formData.append('description', trainingUploadState.description);
      formData.append('version_label', trainingUploadState.version_label);
      formData.append('notes', trainingUploadState.notes);
      formData.append('document_id', trainingUploadState.document_id);
      formData.append('is_mandatory', String(trainingUploadState.is_mandatory));
      formData.append('publish_now', String(trainingUploadState.publish_now));
      formData.append('file', trainingUploadState.file);
      await manualAPI.uploadTrainingDocument(formData);
      toast.success('Documento PDF carregado com sucesso.');
      setTrainingUploadOpen(false);
      setTrainingUploadState({
        title: '',
        category: 'Procedimentos Operacionais',
        description: '',
        version_label: '',
        notes: '',
        document_id: '',
        is_mandatory: true,
        publish_now: true,
        file: null,
      });
      await loadTrainingDocuments();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível carregar o documento.');
    } finally {
      setActiveTrainingActionKey('');
    }
  };

  const handleDownloadProfileSop = async (roleKey = selectedProfileGuide?.role_key, title = selectedProfileGuide?.title) => {
    if (!roleKey) {
      toast.error('Seleccione um perfil antes de exportar o SOP.');
      return;
    }

    setDownloadingProfileRole(roleKey);
    try {
      const response = await manualAPI.downloadProfileSop(roleKey);
      const fallbackFilename = `CG_Business_ERP_SOP_${roleKey}.pdf`;
      const filename = getFilenameFromDisposition(response?.headers?.['content-disposition'], fallbackFilename);
      downloadBlobFile(response.data, filename);
      toast.success(`SOP do perfil ${title || roleKey} exportado com sucesso.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível exportar o SOP deste perfil.');
    } finally {
      setDownloadingProfileRole('');
    }
  };

  const handleRestartTour = async () => {
    try {
      await resetWelcomeTour();
      toast.success('Tour reiniciado. O percurso guiado vai abrir no Dashboard.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível reiniciar o tour.');
    }
  };

  const loadManualInfo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await manualAPI.getInfo();
      const data = response?.data || EMPTY_INFO;
      setManualInfo({ ...EMPTY_INFO, ...data });
    } catch {
      toast.error('Não foi possível carregar o About actualizado.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAboutPageData = useCallback(async () => {
    await Promise.all([loadManualInfo(), loadTrainingDocuments()]);
  }, [loadManualInfo, loadTrainingDocuments]);

  const hasBootstrappedRef = useRef(null);
  if (hasBootstrappedRef.current == null) {
    hasBootstrappedRef.current = true;
    Promise.resolve().then(() => {
      void loadAboutPageData();
    });
  }

  const openEditor = (mode) => {
    setEditorMode(mode);
    setEditorOpen(true);
  };

  const handleSaveEditor = async () => {
    const payload = buildPayloadFromEditor(resolvedEditorState);
    setSaving(true);
    try {
      if (editorMode === 'publish') {
        await manualAPI.publishRelease(payload);
        toast.success('Nova versão publicada no About.');
      } else {
        await manualAPI.updateCurrentRelease(payload);
        toast.success('Release actualizada no About.');
      }
      setEditorOpen(false);
      setEditorState({});
      await loadManualInfo();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível guardar a actualização.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(246,147,31,0.12),_transparent_28%),radial-gradient(circle_at_top_right,_rgba(43,77,170,0.12),_transparent_24%),linear-gradient(180deg,#f8fafc_0%,#ffffff_48%,#eef2ff_100%)] px-4 py-4 sm:px-6"
      data-testid="about-page"
    >
      <div className="mx-auto max-w-[1600px] space-y-4">
        <Card className="overflow-hidden rounded-[28px] border border-slate-200 bg-white/90 shadow-[0_18px_60px_-30px_rgba(15,23,42,0.35)]" data-testid="about-hero-card">
          <CardContent className="p-4 sm:p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="rounded-full bg-[#2B4DAA] px-3 py-1 text-white" data-testid="about-current-semantic-version-badge">{versionLabel}</Badge>
                  <Badge variant="outline" className="rounded-full border-[#F6931F] text-[#F6931F]" data-testid="about-current-date-version-badge">{manualInfo.date_version || '--'}</Badge>
                  <Badge variant="outline" className="rounded-full border-emerald-500 text-emerald-600" data-testid="about-current-status-badge">{manualInfo.go_live_status}</Badge>
                </div>
                <div>
                  <h1 className="text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl" data-testid="about-page-title">About · Versões · Manual</h1>
                  <p className="mt-2 max-w-4xl text-sm text-slate-600 sm:text-base" data-testid="about-release-summary-text">
                    {manualInfo.release_summary}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700" data-testid="about-manual-focus-card">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                    <Wand2 className="h-3.5 w-3.5 text-[#F6931F]" />
                    <span>Manual vivo</span>
                  </div>
                  <p className="mt-2">{manualInfo.manual_focus}</p>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="about-system-meta-grid">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="about-system-version-card">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Versão do sistema</div>
                    <div className="mt-1 text-sm font-semibold text-slate-950">{trainingSystemMeta.system_version || versionLabel}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="about-last-deploy-card">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Último deploy</div>
                    <div className="mt-1 text-sm font-semibold text-slate-950">{formatDateTimeLabel(trainingSystemMeta.last_deploy_at)}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="about-environment-card">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Ambiente</div>
                    <div className="mt-1 text-sm font-semibold text-slate-950">{trainingSystemMeta.environment || 'Preview'}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="about-changelog-card">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Histórico de alterações</div>
                    <div className="mt-1 text-sm font-semibold text-slate-950">{releaseHistory.length || 0} versões registadas</div>
                  </div>
                </div>
              </div>

              <div className="grid w-full gap-3 sm:grid-cols-2 xl:max-w-[520px]" data-testid="about-stats-grid">
                <MiniStat icon={GitBranch} label="Versão actual" value={`${versionLabel} • ${manualInfo.date_version || '--'}`} testId="about-current-version-stat" />
                <MiniStat icon={Calendar} label="Última actualização" value={manualInfo.last_updated || '--'} testId="about-last-updated-stat" />
                <MiniStat icon={Boxes} label="Módulos activos" value={manualInfo.modules || 0} testId="about-modules-stat" />
                <MiniStat icon={Users} label="Perfis documentados" value={manualInfo.access_levels || 0} testId="about-profiles-stat" />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 xl:grid-cols-[1.45fr_1fr]">
          <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-actions-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <BookOpen className="h-4 w-4 text-[#2B4DAA]" />
                Acções rápidas de documentação
              </CardTitle>
              <CardDescription>Manual, dossier técnico, relatório final e controlo da versão actual.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <Button onClick={handleDownloadManual} disabled={downloading} className="justify-start gap-2 rounded-2xl bg-[#F6931F] text-white hover:bg-[#e8850f]" data-testid="download-manual-btn">
                  {downloading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Descarregar Manual
                </Button>
                <Button variant="outline" onClick={() => handleDownloadDoc('/api/manual/dossier-tecnico', 'Download do dossier técnico iniciado.')} className="justify-start gap-2 rounded-2xl" data-testid="download-dossier-btn">
                  <BookOpen className="h-4 w-4" />
                  Dossier Técnico
                </Button>
                <Button variant="outline" onClick={() => handleDownloadDoc('/api/manual/go-live-report', 'Download do relatório go-live iniciado.')} className="justify-start gap-2 rounded-2xl" data-testid="download-go-live-report-btn">
                  <FileText className="h-4 w-4" />
                  Relatório Go-Live
                </Button>
                <Button variant="outline" onClick={handleRestartTour} className="justify-start gap-2 rounded-2xl border-[#2B4DAA] text-[#2B4DAA]" data-testid="restart-tour-btn">
                  <PlayCircle className="h-4 w-4" />
                  Reiniciar Tour
                </Button>
                {canManageRelease && (
                  <Button variant="outline" onClick={() => openEditor('update')} className="justify-start gap-2 rounded-2xl border-emerald-600 text-emerald-700" data-testid="about-edit-current-release-button">
                    <Sparkles className="h-4 w-4" />
                    Actualizar release actual
                  </Button>
                )}
                {canManageRelease && (
                  <Button onClick={() => openEditor('publish')} className="justify-start gap-2 rounded-2xl bg-[#2B4DAA] text-white hover:bg-[#1f3d8f]" data-testid="about-publish-release-button">
                    <GitBranch className="h-4 w-4" />
                    Publicar nova versão
                  </Button>
                )}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600" data-testid="about-release-title-box">
                <strong className="text-slate-900">Release actual:</strong> {manualInfo.release_title}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-system-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Shield className="h-4 w-4 text-[#F6931F]" />
                Sistema e propriedade
              </CardTitle>
              <CardDescription>Informação institucional da instalação actual.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-700">
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="about-system-edition-row">
                <span>Edição</span>
                <span className="font-semibold text-slate-950" data-testid="about-system-edition-value">{manualInfo.edition}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="about-system-last-updated-row">
                <span>Última Actualização</span>
                <span className="font-semibold text-slate-950" data-testid="about-system-last-updated-value">{manualInfo.last_updated}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="about-system-company-row">
                <span>Empresa</span>
                <span className="font-semibold text-slate-950">{manualInfo.company || 'CG Business, Lda'}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="about-system-author-row">
                <span>Autor</span>
                <span className="font-semibold text-slate-950">{manualInfo.author || 'Charles Gabell'}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="about-system-country-row">
                <span>Local</span>
                <span className="inline-flex items-center gap-2 font-semibold text-slate-950"><Globe className="h-4 w-4 text-[#2B4DAA]" /> {manualInfo.location || 'Maputo, Moçambique'}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
          <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-current-profile-context-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4 text-[#2B4DAA]" />
                Sobre o seu perfil / contexto real
              </CardTitle>
              <CardDescription>Leitura contextual por perfil baseada nos acessos efectivos documentados no ERP actual.</CardDescription>
            </CardHeader>
            <CardContent>
              {currentProfileGuide ? (
                <div className="space-y-3" data-testid="about-current-profile-context-body">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-lg font-semibold text-slate-950" data-testid="about-current-profile-title">{currentProfileGuide.title}</div>
                      <div className="mt-1 text-xs text-slate-500">Entrada sugerida: {currentProfileGuide.landing_page || '/dashboard'}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="rounded-full" data-testid="about-current-profile-modules-badge">{currentProfileGuide.visible_modules?.length || 0} módulos visíveis</Badge>
                      <Badge variant="outline" className="rounded-full" data-testid="about-current-profile-flows-badge">{currentProfileGuide.screen_flows?.length || 0} fluxos</Badge>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700" data-testid="about-current-profile-summary">
                    {currentProfileGuide.about_context || currentProfileGuide.summary}
                  </div>

                  <div className="grid gap-3 xl:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="about-current-profile-can-do-card">
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">O que faz</div>
                      <div className="mt-2 space-y-2 text-sm text-slate-700">
                        {(currentProfileGuide.can_do || []).slice(0, 4).map((item, index) => (
                          <div key={`can-do-${index}`} className="rounded-xl border border-slate-200 bg-white px-2.5 py-2" data-testid={`about-current-profile-can-do-${index}`}>{item}</div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="about-current-profile-alerts-card">
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Alertas / cuidados</div>
                      <div className="mt-2 space-y-2 text-sm text-slate-700">
                        {(currentProfileGuide.operational_alerts || []).slice(0, 4).map((item, index) => (
                          <div key={`alert-${index}`} className="rounded-xl border border-slate-200 bg-white px-2.5 py-2" data-testid={`about-current-profile-alert-${index}`}>{item}</div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="about-current-profile-limits-card">
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Limites / fronteiras</div>
                      <div className="mt-2 space-y-2 text-sm text-slate-700">
                        {(currentProfileGuide.access_limits || []).slice(0, 4).map((item, index) => (
                          <div key={`limit-${index}`} className="rounded-xl border border-slate-200 bg-white px-2.5 py-2" data-testid={`about-current-profile-limit-${index}`}>{item}</div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2" data-testid="about-current-profile-module-chips">
                    {(currentProfileGuide.visible_modules || []).map((module) => (
                      <Badge key={`${currentProfileGuide.role_key}-${module.key}`} variant="outline" className="rounded-full text-[11px]">{module.name}</Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500" data-testid="about-current-profile-context-empty">
                  Não foi possível resolver automaticamente o contexto documental do perfil deste utilizador.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-documentation-sync-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-4 w-4 text-[#F6931F]" />
                Sincronização documental viva
              </CardTitle>
              <CardDescription>Modelo que liga permissões reais, perfil, About, Tour, guias rápidos e SOP por perfil.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-700">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="about-documentation-sync-mode">
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Modo</div>
                <div className="mt-1 font-semibold text-slate-950">{documentationSync.mode || 'Sem dados'}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="about-documentation-sync-last-scan">
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Última varredura contextual</div>
                <div className="mt-1 font-semibold text-slate-950">{documentationSync.last_context_scan || '--'}</div>
              </div>
              <div className="space-y-2" data-testid="about-documentation-sync-rules">
                {(documentationSync.sync_rules || []).map((rule, index) => (
                  <div key={`sync-rule-${index}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2" data-testid={`about-documentation-sync-rule-${index}`}>{rule}</div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-training-documents-card">
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShieldCheck className="h-4 w-4 text-[#2B4DAA]" />
                  Documentação e Formação
                </CardTitle>
                <CardDescription>PDFs versionados no servidor, leitura, download, aceite por versão e relatórios administrativos.</CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                {canViewTrainingReports && (
                  <Button variant="outline" onClick={handleExportTrainingReport} className="rounded-2xl" data-testid="about-training-export-report-button">
                    <FileText className="mr-2 h-4 w-4" />
                    Exportar relatório
                  </Button>
                )}
                {canManageTraining && (
                  <Button onClick={() => setTrainingUploadOpen(true)} className="rounded-2xl bg-[#2B4DAA] text-white hover:bg-[#1f3d8f]" data-testid="about-training-upload-button">
                    <Upload className="mr-2 h-4 w-4" />
                    Carregar PDF
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="about-training-metrics-grid">
              <MiniStat icon={BookOpen} label="Documentos" value={trainingMeta.total_documents || 0} testId="about-training-total-documents-stat" />
              <MiniStat icon={Lock} label="Obrigatórios" value={trainingMeta.mandatory_documents || 0} testId="about-training-mandatory-documents-stat" />
              <MiniStat icon={AlertTriangle} label="Pendentes" value={trainingMeta.pending_required_documents || 0} testId="about-training-pending-documents-stat" />
              <MiniStat icon={Download} label="Versões publicadas" value={publishedVersionCount} testId="about-training-published-versions-stat" />
            </div>

            <div className="grid gap-3 xl:grid-cols-[1.2fr_0.8fr]">
              <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4" data-testid="about-training-search-card">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-950">Pesquisa de documentos</div>
                    <div className="mt-1 text-xs text-slate-500">Pesquise por título, categoria e versão.</div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-2xl"
                    onClick={() => setTrainingFilters({ search: '', category: '', version: '' })}
                    data-testid="about-training-clear-filters-button"
                  >
                    Limpar filtros
                  </Button>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="training-search">Pesquisar</Label>
                    <Input
                      id="training-search"
                      value={trainingFilters.search}
                      onChange={(event) => setTrainingFilters((prev) => ({ ...prev, search: event.target.value }))}
                      placeholder="Ex: inventário, compras..."
                      data-testid="about-training-search-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Categoria</Label>
                    <Select value={trainingFilters.category || 'all'} onValueChange={(value) => setTrainingFilters((prev) => ({ ...prev, category: value === 'all' ? '' : value }))}>
                      <SelectTrigger className="rounded-2xl bg-white" data-testid="about-training-category-filter">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {availableTrainingCategories.map((item) => (
                          <SelectItem key={item} value={item}>{item}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="training-version-search">Versão</Label>
                    <Input
                      id="training-version-search"
                      value={trainingFilters.version}
                      onChange={(event) => setTrainingFilters((prev) => ({ ...prev, version: event.target.value }))}
                      placeholder="Ex: v1.0"
                      data-testid="about-training-version-filter"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4" data-testid="about-training-user-indicators-card">
                <div className="text-sm font-semibold text-slate-950">Indicadores do colaborador</div>
                <div className="mt-1 text-xs text-slate-500">Resumo pessoal de leitura e aceite por versão.</div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3" data-testid="about-training-user-pending-read-stat">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Pendentes de leitura</div>
                    <div className="mt-1 text-lg font-semibold text-slate-950">{trainingMeta.pending_read_documents || 0}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3" data-testid="about-training-user-pending-accept-stat">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Pendentes de aceite</div>
                    <div className="mt-1 text-lg font-semibold text-slate-950">{trainingMeta.pending_accept_documents || 0}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4" data-testid="about-training-compliance-card">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-950">Conformidade individual de leitura e aceite</div>
                  <div className="mt-1 text-xs text-slate-500">Cada nova versão obrigatória exige novo aceite e só pode ser aceite depois de abrir ou descarregar.</div>
                </div>
                <Badge variant="outline" className="rounded-full border-emerald-300 bg-emerald-50 text-emerald-700" data-testid="about-training-compliance-badge">
                  {mandatoryCompletionPercent}% concluído
                </Badge>
              </div>
              <div className="mt-3">
                <Progress value={mandatoryCompletionPercent} data-testid="about-training-compliance-progress" />
              </div>
            </div>

            {canViewTrainingReports && (
              <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4" data-testid="about-training-admin-dashboard-card">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-950">Dashboard para Administração</div>
                    <div className="mt-1 text-xs text-slate-500">Leitura, aceite, pendências e conformidade por documento.</div>
                  </div>
                  <Badge variant="outline" className="rounded-full" data-testid="about-training-admin-total-users-badge">
                    {trainingDashboard.total_users || 0} utilizadores
                  </Badge>
                </div>
                <div className="mt-3 grid gap-3 lg:grid-cols-2">
                  {(trainingDashboard.documents || []).map((entry) => (
                    <div key={entry.document_id} className="rounded-2xl border border-slate-200 bg-white px-4 py-4" data-testid={`about-training-admin-document-${entry.document_id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="text-sm font-semibold text-slate-950">{entry.title}</div>
                        <Badge variant="outline" className="rounded-full">{entry.category}</Badge>
                        {entry.is_mandatory ? <Badge className="rounded-full bg-rose-100 text-rose-700">Obrigatório</Badge> : <Badge variant="outline" className="rounded-full">Opcional</Badge>}
                      </div>
                      <div className="mt-2 text-xs text-slate-500">Versão activa: {entry.active_version_label || '—'}</div>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">Leram: <strong>{entry.read_count}</strong></div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">Aceitaram: <strong>{entry.accept_count}</strong></div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">Pendentes: <strong>{entry.pending_count}</strong></div>
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">Conformidade: <strong>{entry.compliance_percent}%</strong></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {trainingLoading ? (
              <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500" data-testid="about-training-loading-state">A carregar documentos e histórico de aceites...</div>
            ) : trainingDocuments.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500" data-testid="about-training-empty-state">Ainda não existem documentos publicados nesta secção.</div>
            ) : (
              <div className="space-y-4" data-testid="about-training-documents-list">
                {trainingDocuments.map((document, documentIndex) => (
                  <div key={document.id} className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4" data-testid={`about-training-document-card-${documentIndex}`}>
                    <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-base font-semibold text-slate-950" data-testid={`about-training-document-title-${document.id}`}>{document.title}</div>
                          <Badge variant="outline" className="rounded-full">{document.category}</Badge>
                          {document.is_mandatory ? <Badge className="rounded-full bg-rose-100 text-rose-700">Obrigatório</Badge> : <Badge variant="outline" className="rounded-full">Opcional</Badge>}
                          {document.has_pending_acceptance ? <Badge className="rounded-full bg-amber-100 text-amber-700">Pendente de aceite</Badge> : <Badge className="rounded-full bg-emerald-100 text-emerald-700">Em conformidade</Badge>}
                        </div>
                        <p className="text-sm text-slate-600" data-testid={`about-training-document-description-${document.id}`}>{document.description || 'Sem descrição adicional.'}</p>
                        <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                          <span>Criado: {formatDateTimeLabel(document.created_at)}</span>
                          <span>Actualizado: {formatDateTimeLabel(document.updated_at)}</span>
                          <span>Versões: {document.latest_version_number || 1}</span>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4" data-testid={`about-training-document-indicators-${document.id}`}>
                          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
                            <div className="uppercase tracking-[0.16em] text-slate-500">Pendência de leitura</div>
                            <div className="mt-1 font-semibold text-slate-950">{document.user_summary?.pending_read ? 'Sim' : 'Não'}</div>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
                            <div className="uppercase tracking-[0.16em] text-slate-500">Pendência de aceite</div>
                            <div className="mt-1 font-semibold text-slate-950">{document.user_summary?.pending_accept ? 'Sim' : 'Não'}</div>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
                            <div className="uppercase tracking-[0.16em] text-slate-500">Última versão lida</div>
                            <div className="mt-1 font-semibold text-slate-950">{document.user_summary?.last_read_version || '—'}</div>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
                            <div className="uppercase tracking-[0.16em] text-slate-500">Última versão aceite</div>
                            <div className="mt-1 font-semibold text-slate-950">{document.user_summary?.last_accepted_version || '—'}</div>
                          </div>
                        </div>
                      </div>
                      {canManageTraining && (
                        <Button
                          variant="outline"
                          onClick={() => setTrainingUploadState((prev) => ({
                            ...prev,
                            title: document.title,
                            category: document.category || 'Procedimentos Operacionais',
                            description: document.description || '',
                            document_id: document.id,
                            version_label: '',
                            notes: '',
                            file: null,
                            is_mandatory: document.is_mandatory,
                            publish_now: true,
                          })) || setTrainingUploadOpen(true)}
                          className="rounded-2xl"
                          data-testid={`about-training-add-version-button-${document.id}`}
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          Nova versão
                        </Button>
                      )}
                    </div>

                    <div className="mt-4 space-y-3">
                      {(document.versions || []).map((version, versionIndex) => {
                        const versionActionKeyPrefix = `${document.id}-${version.id}`;
                        return (
                          <div key={version.id} className="rounded-2xl border border-slate-200 bg-white px-4 py-4" data-testid={`about-training-version-card-${document.id}-${versionIndex}`}>
                            <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="text-sm font-semibold text-slate-950" data-testid={`about-training-version-label-${version.id}`}>{version.version_label}</div>
                                  {version.is_published ? <Badge className="rounded-full bg-[#2B4DAA] text-white">Publicado</Badge> : <Badge variant="outline" className="rounded-full">Rascunho</Badge>}
                                  {version.pending_acceptance ? <Badge className="rounded-full bg-amber-100 text-amber-700">Aceite pendente</Badge> : null}
                                  {version.user_status?.accepted_at ? <Badge className="rounded-full bg-emerald-100 text-emerald-700">Aceite em {formatDateTimeLabel(version.user_status.accepted_at)}</Badge> : null}
                                </div>
                                <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                                  <span>Ficheiro: {version.original_filename}</span>
                                  <span>Tamanho: {formatFileSize(version.size)}</span>
                                  <span>Criado: {formatDateTimeLabel(version.created_at)}</span>
                                  <span>Publicado: {formatDateTimeLabel(version.published_at)}</span>
                                </div>
                                <p className="text-sm text-slate-600">{version.notes || 'Sem notas desta versão.'}</p>
                                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4" data-testid={`about-training-version-user-status-${version.id}`}>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">Leitura: <strong>{version.user_status?.has_viewed ? 'Sim' : 'Não'}</strong></div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">Download: <strong>{version.user_status?.has_downloaded ? 'Sim' : 'Não'}</strong></div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">Última leitura: <strong>{formatDateTimeLabel(version.user_status?.last_viewed_at || version.user_status?.last_downloaded_at)}</strong></div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">Último aceite: <strong>{formatDateTimeLabel(version.user_status?.accepted_at)}</strong></div>
                                </div>
                                <div className="flex flex-wrap gap-2 text-xs">
                                  <Badge variant="outline" className="rounded-full">Vistas: {version.admin_metrics?.view || 0}</Badge>
                                  <Badge variant="outline" className="rounded-full">Downloads: {version.admin_metrics?.download || 0}</Badge>
                                  <Badge variant="outline" className="rounded-full">Aceites: {version.admin_metrics?.accept || 0}</Badge>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 xl:justify-end">
                                <Button
                                  variant="outline"
                                  onClick={() => handleOpenTrainingDocument(document.id, version.id, document.title)}
                                  disabled={activeTrainingActionKey === `open-${versionActionKeyPrefix}`}
                                  className="rounded-2xl"
                                  data-testid={`about-training-open-button-${version.id}`}
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  Ver PDF
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => handleDownloadTrainingDocument(document.id, version.id, document.title, version.original_filename)}
                                  disabled={activeTrainingActionKey === `download-${versionActionKeyPrefix}`}
                                  className="rounded-2xl"
                                  data-testid={`about-training-download-button-${version.id}`}
                                >
                                  <Download className="mr-2 h-4 w-4" />
                                  Baixar PDF
                                </Button>
                                <Button
                                  onClick={() => handleAcceptTrainingDocument(document.id, version.id, document.title)}
                                  disabled={!version.user_status?.can_accept || activeTrainingActionKey === `accept-${versionActionKeyPrefix}`}
                                  className="rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-300"
                                  data-testid={`about-training-accept-button-${version.id}`}
                                >
                                  <ShieldCheck className="mr-2 h-4 w-4" />
                                  Confirmar aceite
                                </Button>
                                {canManageTraining && (
                                  <Button
                                    variant="outline"
                                    onClick={() => handleTogglePublication(document.id, version.id, !version.is_published)}
                                    disabled={activeTrainingActionKey === `publish-${versionActionKeyPrefix}`}
                                    className="rounded-2xl"
                                    data-testid={`about-training-publish-toggle-button-${version.id}`}
                                  >
                                    {version.is_published ? 'Despublicar' : 'Publicar'}
                                  </Button>
                                )}
                              </div>
                            </div>

                            {canManageTraining && version.recent_activity?.length > 0 && (
                              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`about-training-recent-activity-${version.id}`}>
                                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Actividade recente desta versão</div>
                                <div className="grid gap-2 xl:grid-cols-2">
                                  {version.recent_activity.map((event, eventIndex) => (
                                    <div key={event.id || `${version.id}-activity-${eventIndex}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600" data-testid={`about-training-activity-item-${version.id}-${eventIndex}`}>
                                      <div className="font-semibold text-slate-900">{event.user_name}</div>
                                      <div>{event.event_type_label || event.event_type} · {event.user_role || 'sem perfil'} · {event.device_label || 'dispositivo desconhecido'}</div>
                                      <div>{formatDateTimeLabel(event.created_at)}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
          <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-current-release-notes-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ClipboardList className="h-4 w-4 text-[#2B4DAA]" />
                O que esta versão inclui
              </CardTitle>
              <CardDescription>Notas activas desta versão do sistema.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {loading ? (
                <div className="rounded-2xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500" data-testid="about-loading-state">A carregar About actualizado...</div>
              ) : releaseNotes.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500" data-testid="about-empty-release-notes">Sem notas publicadas para a versão actual.</div>
              ) : (
                releaseNotes.map((note, index) => (
                  <div key={note.id || `${note.title}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`about-release-note-${index}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-950">{note.title || note.summary}</span>
                      {note.date_label ? <Badge variant="outline" className="rounded-full">{note.date_label}</Badge> : null}
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{note.summary}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-modules-overview-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Boxes className="h-4 w-4 text-[#F6931F]" />
                Mapa rápido dos módulos
              </CardTitle>
              <CardDescription>Resumo compacto para onboarding e revisão operacional.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {moduleOverview.map((module, index) => (
                  <div key={module.key || `${module.name}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`about-module-card-${index}`}>
                    <div className="text-sm font-semibold text-slate-950">{module.name}</div>
                    <div className="mt-1 text-xs text-slate-500">{module.route}</div>
                    <p className="mt-2 text-xs text-slate-600">{module.summary}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-profile-sop-export-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4 text-[#2B4DAA]" />
              Export SOP por perfil
            </CardTitle>
            <CardDescription>Seleccione um perfil já documentado no About e exporte um PDF específico, pronto para consulta e impressão.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600">Perfil documentado</label>
                <Select value={resolvedSelectedProfileRole || ''} onValueChange={setSelectedProfileRole}>
                  <SelectTrigger className="rounded-2xl bg-white" data-testid="about-profile-sop-select">
                    <SelectValue placeholder="Seleccionar perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    {profileGuides.map((guide) => (
                      <SelectItem key={guide.role_key} value={guide.role_key}>
                        {guide.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={() => handleDownloadProfileSop()}
                disabled={!selectedProfileGuide || downloadingProfileRole === selectedProfileGuide?.role_key}
                className="justify-start gap-2 rounded-2xl bg-[#2B4DAA] text-white hover:bg-[#1f3d8f]"
                data-testid="about-profile-sop-download-button"
              >
                {downloadingProfileRole === selectedProfileGuide?.role_key ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Exportar SOP em PDF
              </Button>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4" data-testid="about-profile-sop-summary-card">
              {selectedProfileGuide ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-950">{selectedProfileGuide.title}</div>
                      <div className="mt-1 text-xs text-slate-500">Entrada sugerida: {selectedProfileGuide.landing_page || '/dashboard'}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="rounded-full">{selectedProfileGuide.primary_modules?.length || 0} módulos</Badge>
                      <Badge variant="outline" className="rounded-full">{selectedProfileGuide.screen_flows?.length || 0} fluxos</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">{selectedProfileGuide.summary}</p>
                  <div className="flex flex-wrap gap-2">
                    {(selectedProfileGuide.primary_modules || []).map((module) => (
                      <Badge key={`${selectedProfileGuide.role_key}-${module}-sop`} variant="outline" className="rounded-full text-[11px]">{module}</Badge>
                    ))}
                  </div>
                  <div className="grid gap-2 xl:grid-cols-2">
                    {(selectedProfileGuide.can_do || []).slice(0, 4).map((item, index) => (
                      <div key={`sop-can-do-${index}`} className="rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700" data-testid={`about-profile-sop-can-do-${index}`}>{item}</div>
                    ))}
                  </div>
                  {(selectedProfileGuide.access_limits || []).length > 0 ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="about-profile-sop-limits-box">
                      <strong>Limites:</strong> {(selectedProfileGuide.access_limits || []).slice(0, 2).join(' • ')}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-sm text-slate-500">Sem perfis documentados disponíveis para exportar.</div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-profile-guides-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-[#2B4DAA]" />
              Guias rápidos por perfil
            </CardTitle>
            <CardDescription>O tour e o manual passam a seguir este mapa funcional por função de utilizador.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
              {profileGuides.map((guide, index) => (
                <div key={guide.role_key || `${guide.title}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`about-profile-guide-${index}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-950">{guide.title}</div>
                      <div className="text-xs text-slate-500">Entrada sugerida: {guide.landing_page || '/dashboard'}</div>
                    </div>
                    <Badge variant="outline" className="rounded-full">{guide.primary_modules?.length || 0} módulos</Badge>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{guide.summary}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(guide.primary_modules || []).map((module) => (
                      <Badge key={`${guide.role_key}-${module}`} variant="outline" className="rounded-full text-[11px]">{module}</Badge>
                    ))}
                  </div>
                  {(guide.can_do || []).length > 0 ? (
                    <div className="mt-3 space-y-1.5 text-xs text-slate-600" data-testid={`about-profile-capabilities-${index}`}>
                      {(guide.can_do || []).slice(0, 3).map((item, itemIndex) => (
                        <div key={`${guide.role_key}-capability-${itemIndex}`} className="rounded-xl border border-slate-200 bg-white px-2.5 py-2">{item}</div>
                      ))}
                    </div>
                  ) : null}
                  {(guide.tour_quick_path || []).length > 0 ? (
                    <div className="mt-3 rounded-xl border border-[#F6931F]/20 bg-[#fff7ed] px-2.5 py-2 text-xs text-slate-700" data-testid={`about-profile-tour-path-${index}`}>
                      <div className="mb-1 font-semibold text-[#b96405]">Roteiro rápido do tour</div>
                      <div className="space-y-1">
                        {(guide.tour_quick_path || []).map((item, itemIndex) => (
                          <div key={`${guide.role_key}-tour-${itemIndex}`}>• {item}</div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-3 space-y-1.5 text-xs text-slate-600">
                    {(guide.daily_routine || []).slice(0, 3).map((step, stepIndex) => (
                      <div key={`${guide.role_key}-routine-${stepIndex}`} className="rounded-xl border border-slate-200 bg-white px-2.5 py-2" data-testid={`about-profile-routine-${index}-${stepIndex}`}>
                        {step}
                      </div>
                    ))}
                  </div>
                  {(guide.access_limits || []).length > 0 ? (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800" data-testid={`about-profile-limit-box-${index}`}>
                      {(guide.access_limits || []).slice(0, 2).map((item, itemIndex) => <div key={`${guide.role_key}-limit-${itemIndex}`}>• {item}</div>)}
                    </div>
                  ) : null}
                  <Button
                    variant="outline"
                    onClick={() => handleDownloadProfileSop(guide.role_key, guide.title)}
                    disabled={downloadingProfileRole === guide.role_key}
                    className="mt-3 w-full justify-center gap-2 rounded-2xl"
                    data-testid={`about-profile-sop-card-button-${index}`}
                  >
                    {downloadingProfileRole === guide.role_key ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Exportar SOP deste perfil
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-screen-flows-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList className="h-4 w-4 text-[#F6931F]" />
              Fluxos operacionais detalhados por função
            </CardTitle>
            <CardDescription>Passo a passo por ecrã para onboarding, execução diária e auditoria interna.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 2xl:grid-cols-2">
              {profileGuides.map((guide, index) => (
                <ProfileWorkflowCard key={guide.role_key || `${guide.title}-${index}`} guide={guide} guideIndex={index} />
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-release-history-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <GitBranch className="h-4 w-4 text-[#F6931F]" />
              Histórico de versões do sistema
            </CardTitle>
            <CardDescription>Cada versão publicada fica registada e visível no próprio About.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {releaseHistory.map((release, index) => (
              <div key={release.id || `${release.version_label}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`about-release-history-row-${index}`}>
                <div className="flex flex-col gap-2 xl:flex-row xl:items-start xl:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-950">{release.version_label || `v${release.version || '--'}`}</span>
                      <Badge variant={release.is_current ? 'default' : 'outline'} className="rounded-full">{release.is_current ? 'Actual' : 'Histórico'}</Badge>
                      <Badge variant="outline" className="rounded-full">{release.source || 'auto'}</Badge>
                    </div>
                    <div className="mt-1 text-sm font-medium text-slate-800">{release.release_title || release.edition}</div>
                    <p className="mt-1 text-sm text-slate-600">{release.release_summary}</p>
                  </div>
                  <div className="text-xs text-slate-500">{release.last_updated || '--'}</div>
                </div>
                {(release.release_notes || []).length > 0 ? (
                  <div className="mt-3 grid gap-2 xl:grid-cols-3">
                    {(release.release_notes || []).slice(0, 3).map((note, noteIndex) => (
                      <div key={note.id || `${release.id}-note-${noteIndex}`} className="rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-600" data-testid={`about-release-history-note-${index}-${noteIndex}`}>
                        {note.summary || note.title}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="pb-2 text-center text-xs text-slate-500" data-testid="about-footer-text">
          {manualInfo.product_name || 'CG Business ERP'} • {versionLabel} • {manualInfo.edition} • {manualInfo.last_updated}
        </div>
      </div>

      <Dialog open={trainingUploadOpen} onOpenChange={setTrainingUploadOpen}>
        <DialogContent className="max-w-2xl" data-testid="about-training-upload-dialog">
          <DialogHeader>
            <DialogTitle>{trainingUploadState.document_id ? 'Nova versão de documento' : 'Carregar novo PDF de formação'}</DialogTitle>
            <DialogDescription>Disponível para Admin, Gerência e Contabilidade/RH conforme permissões do sistema.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="training-title">Título</Label>
                <Input id="training-title" value={trainingUploadState.title} onChange={(event) => setTrainingUploadState((prev) => ({ ...prev, title: event.target.value }))} data-testid="about-training-upload-title-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="training-category">Categoria</Label>
                <Select value={trainingUploadState.category || 'Procedimentos Operacionais'} onValueChange={(value) => setTrainingUploadState((prev) => ({ ...prev, category: value }))}>
                  <SelectTrigger className="rounded-2xl bg-white" data-testid="about-training-upload-category-input">
                    <SelectValue placeholder="Seleccionar categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTrainingCategories.map((item) => (
                      <SelectItem key={item} value={item}>{item}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="training-description">Descrição</Label>
              <Textarea id="training-description" value={trainingUploadState.description} onChange={(event) => setTrainingUploadState((prev) => ({ ...prev, description: event.target.value }))} data-testid="about-training-upload-description-input" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="training-version-label">Etiqueta da versão</Label>
                <Input id="training-version-label" placeholder="Ex: v2.0" value={trainingUploadState.version_label} onChange={(event) => setTrainingUploadState((prev) => ({ ...prev, version_label: event.target.value }))} data-testid="about-training-upload-version-label-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="training-file">PDF</Label>
                <Input id="training-file" type="file" accept="application/pdf,.pdf" onChange={(event) => setTrainingUploadState((prev) => ({ ...prev, file: event.target.files?.[0] || null }))} data-testid="about-training-upload-file-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="training-notes">Notas da versão</Label>
              <Textarea id="training-notes" value={trainingUploadState.notes} onChange={(event) => setTrainingUploadState((prev) => ({ ...prev, notes: event.target.value }))} data-testid="about-training-upload-notes-input" />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <button type="button" onClick={() => setTrainingUploadState((prev) => ({ ...prev, is_mandatory: !prev.is_mandatory }))} className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${trainingUploadState.is_mandatory ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-slate-200 bg-white text-slate-700'}`} data-testid="about-training-upload-mandatory-toggle">
                <div className="font-semibold">Documento obrigatório</div>
                <div className="mt-1 text-xs">Cada nova versão obrigatória volta a exigir aceite.</div>
              </button>
              <button type="button" onClick={() => setTrainingUploadState((prev) => ({ ...prev, publish_now: !prev.publish_now }))} className={`rounded-2xl border px-4 py-3 text-left text-sm transition ${trainingUploadState.publish_now ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-700'}`} data-testid="about-training-upload-publish-toggle">
                <div className="font-semibold">Publicar imediatamente</div>
                <div className="mt-1 text-xs">Se desligado, a versão fica em rascunho até publicação manual.</div>
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTrainingUploadOpen(false)} data-testid="about-training-upload-cancel-button">Cancelar</Button>
            <Button type="button" onClick={handleTrainingUploadSubmit} disabled={activeTrainingActionKey === 'training-upload'} className="bg-[#2B4DAA] text-white hover:bg-[#1f3d8f]" data-testid="about-training-upload-submit-button">
              {activeTrainingActionKey === 'training-upload' ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Guardar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl rounded-[28px] border border-slate-200 p-0" data-testid="about-release-editor-dialog">
          <div className="max-h-[88vh] overflow-y-auto p-5 sm:p-6">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-slate-950">
                {editorMode === 'publish' ? 'Publicar nova versão' : 'Actualizar release actual'}
              </DialogTitle>
              <DialogDescription>
                {editorMode === 'publish'
                  ? 'Cria uma nova versão no histórico do About mantendo o registo completo das entregas.'
                  : 'Ajusta a release actual sem criar uma nova linha de versão.'}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600">Título da release</label>
                <Input value={resolvedEditorState.release_title} onChange={(event) => setEditorState((current) => ({ ...current, release_title: event.target.value }))} data-testid="about-release-editor-title-input" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600">Edição</label>
                <Input value={resolvedEditorState.edition} onChange={(event) => setEditorState((current) => ({ ...current, edition: event.target.value }))} data-testid="about-release-editor-edition-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Resumo executivo</label>
                <Textarea value={resolvedEditorState.release_summary} onChange={(event) => setEditorState((current) => ({ ...current, release_summary: event.target.value }))} rows={3} data-testid="about-release-editor-summary-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Estado / Última Actualização</label>
                <Input value={resolvedEditorState.go_live_status} onChange={(event) => setEditorState((current) => ({ ...current, go_live_status: event.target.value }))} data-testid="about-release-editor-status-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Foco do manual</label>
                <Textarea value={resolvedEditorState.manual_focus} onChange={(event) => setEditorState((current) => ({ ...current, manual_focus: event.target.value }))} rows={3} data-testid="about-release-editor-focus-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Notas da versão (uma por linha)</label>
                <Textarea value={resolvedEditorState.release_notes_text} onChange={(event) => setEditorState((current) => ({ ...current, release_notes_text: event.target.value }))} rows={6} data-testid="about-release-editor-notes-input" />
              </div>
            </div>

            <DialogFooter className="mt-5 gap-2">
              <Button variant="outline" onClick={() => { setEditorOpen(false); setEditorState({}); }} data-testid="about-release-editor-cancel-button">Cancelar</Button>
              <Button onClick={handleSaveEditor} disabled={saving} className="bg-[#2B4DAA] text-white hover:bg-[#1f3d8f]" data-testid="about-release-editor-save-button">
                {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
                {editorMode === 'publish' ? 'Publicar versão' : 'Guardar actualização'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}